<template>

  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="fixed z-10 inset-0 xs:m-0.5 sm:m-4 xl:m-12" @keydown.esc="$emit('close')" static>
      <div class="flex items-end justify-center p-4 text-center sm:block sm:p-0 h-full max-h-full">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
                         leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-95 transition-opacity" @click.stop="$emit('close')"/>
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <!--        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>-->

        <TransitionChild as="template" enter="ease-out duration-300"
                         enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                         enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
                         leave-from="opacity-100 translate-y-0 sm:scale-100"
                         leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
          <div :class="[blockUI ? 'filter blur-sm' : '']"
               class="h-full flex flex-col bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:align-middle sm:w-full">
            <!-- Card Container -->

            <!-- Close button-->
            <div class="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
              <button type="button"
                      class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                      @click="$emit('close');">
                <span class="sr-only">Close</span>
                <XIcon class="h-6 w-6" aria-hidden="true"/>
              </button>
            </div>

            <!--header -->
            <div class="p-4 sm:px-6 flex-grow-0">
              <DialogTitle as="h3" class="flex items-center space-x-2 text-lg font-medium leading-6 text-gray-900">
                <OfficeBuildingIcon class="w-6 h-6 text-gray-600"></OfficeBuildingIcon>
                <span>{{ company.name }}</span>
              </DialogTitle>
            </div>

            <!--body -->
            <div class="px-4 pb-5 sm:px-6 flex-grow h-full max-h-full overflow-y-auto xl:overflow-hidden flex flex-col">

              <!-- Tabs -->
              <div class="flex-grow-0">
                <div class="sm:hidden">
                  <label for="mobile-tabs" class="sr-only">Select a tab</label>
                  <!-- Use an "onChange" listener to redirect the user to the selected tab URL. -->
                  <select id="mobile-tabs" name="tabs"
                          class="block py-2 pr-10 pl-3 w-full text-base rounded-md border-gray-300 focus:outline-none sm:text-sm"
                          @change="setCurrentTab($event.target.value)">
                    <option
                        v-for="tab in tabs"
                        :key="tab.name"
                        :selected="tab.current"
                        :value="tab.name"
                    >
                      {{ tab.name }}
                    </option>
                  </select>

                </div>
                <div class="hidden sm:block">
                  <div class="border-b border-gray-200">
                    <nav class="flex -mb-px space-x-4" aria-label="Tabs">
                      <button v-for="tab in tabs"
                              @click="setCurrentTab(tab)"
                              :key="tab.name"
                              :class="[tab.current ? 'border-primary-400 text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm']"
                              :aria-current="tab.current ? 'page' : undefined"
                      >
                        {{ tab.name }}
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
              <!-- End Tabs -->

              <!--Error displaying -->
              <section v-if="errorsToShow.length > 0" class="flex-grow-0 mb-4 mt-2">
                <div v-for="(errorMsg, errorIdx) in errorsToShow" :key="'error-to-show-'+errorIdx"
                     class="p-4 bg-red-50 rounded-md">
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <ExclamationIcon class="w-5 h-5 text-red-500" aria-hidden="true"/>
                    </div>
                    <div class="ml-3 text-red-700 text-sm">
                      <h3 class="font-semibold">Oops!</h3>
                      <div class="mt-1">
                        <p>{{ errorMsg }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <!--End Error displaying -->

              <div class="h-full max-h-full flex-grow xl:overflow-hidden">
                <keep-alive>
                  <component :is="`Company${currentTab.name.replace(' ', '')}`"
                             :="currentProperties"
                             @use-tag="$emit('use-tag', {'tag': $event.tag, 'use':  $event.use})"
                             @attachments:add="attachment => $emit('attachments:add', attachment)"
                             @attachments:remove="attachment => $emit('attachments:remove', attachment)"
                             class="max-h-full">
                  </component>
                </keep-alive>
              </div>

              <save-to-list
                  v-if="openSaveToListDialog"
                  :company="company"
                  :open="openSaveToListDialog"
                  @close="openSaveToListDialog = false"
              >
              </save-to-list>

              <simple-reject-company-dialog
                  v-if="openRejectCompanyDialog"
                  @close="openRejectCompanyDialog=false"
                  :open="openRejectCompanyDialog"
                  :company="companyToReject"
                  :channelUuid="getChannelUuid(currentViewChannel)"
                  :not-relevant-channel-uuid="notRelevantCompaniesChannelUuid"
              >
              </simple-reject-company-dialog>


            </div>
            <!-- End Body -->

            <!--footer -->
            <div class="p-4 sm:px-6 flex-grow-0 border-t">
              <div class="flex items-center justify-center space-x-3 lg:space-x-6"
                   :set="countReports = $parent.companyReportsCount(company)">
                <span v-if="countReports === 0" class="inline-flex items-center text-sm">
                  <button
                      type="button"
                      @click.stop="$emit('request-report-action-report', {company : company})"
                      class="inline-flex p-1 space-x-2 rounded-full border text-primary hover:text-primary-600 border-primary hover:border-primary-600"
                      title="Request a detailed company report">
                    <DownloadIcon class="w-8 h-8" aria-hidden="true"/>
                    <span class="sr-only">Request Report</span>
                  </button>
                </span>
                <span v-if="notRelevantCompanies.indexOf(company.id) === -1" class="inline-flex items-center text-sm">
                  <button
                      v-if="relevantCompanies.indexOf(company.id) === -1"
                      class="inline-flex p-1 space-x-2 rounded-full border text-ternary hover:text-ternary-600 border-ternary hover:border-ternary-600 focus:outline-none focus:ring-1 focus:ring-ternary"
                      title="Mark as 'Interesting' for my search"
                      type="button"
                      @click="addRelevantCompanyToChannel($event, company)">
                    <HeartIcon class="w-8 h-8" aria-hidden="true"/>
                    <span class="sr-only">Relevant</span>
                  </button>
                  <button
                      v-else
                      class="inline-flex p-1 space-x-2 text-white rounded-full border bg-ternary border-ternary hover:bg-white hover:text-gray-600 hover:border-gray-600 focus:outline-none focus:ring-1 focus:ring-ternary"
                      title="Remove from my interesting list"
                      type="button"
                      @click="removeRelevantCompanyToChannel($event, company)">
                    <HeartIcon class="w-8 h-8" aria-hidden="true"/>
                    <span class="sr-only">Relevant</span>
                  </button>
                </span>
                <span v-if="relevantCompanies.indexOf(company.id) === -1" class="inline-flex items-center text-sm">
                  <button
                      v-if="notRelevantCompanies.indexOf(company.id) === -1"
                      @click="addNotRelevantCompanyToChannel($event, company)"
                      type="button"
                      class="inline-flex p-1 space-x-2 text-gray-400 rounded-full border border-gray-400 hover:text-gray-600 hover:border-gray-600 focus:outline-none focus:ring-1 focus:gray-400"
                      title="Mark as 'Not Interesting' for my search">
                    <BanIcon class="w-8 h-8" aria-hidden="true"/>
                    <span class="sr-only">Not relevant</span>
                  </button>
                  <button
                      v-else
                      class="inline-flex p-1 space-x-2 text-white rounded-full border bg-gray-500 border-gray-500 hover:bg-white hover:text-gray-600 hover:border-gray-600 focus:outline-none focus:ring-1 focus:ring-ternary"
                      title="Mark as 'Neutral' for my search"
                      type="button"
                      @click="removeNotRelevantCompanyToChannel($event, company)">
                    <BanIcon class="w-8 h-8" aria-hidden="true"/>
                    <span class="sr-only">Not Relevant</span>
                  </button>
                </span>
                <span v-if="isInCustomCompanyChannel"
                      class="inline-flex items-center text-sm">
                  <button
                      @click="removeCompanyFromChannel($event, company)"
                      type="button"
                      class="inline-flex p-1 space-x-2 text-gray-400 rounded-full border border-gray-400 hover:text-red-600 hover:border-red-600 focus:outline-none focus:ring-1 focus:red-400"
                      title="Remove from the list">
                    <TrashIcon class="w-6 h-6" aria-hidden="true"/>
                    <span class="sr-only">Remove from the list</span>
                  </button>
                </span>


                <span class="inline-flex items-center text-sm">
                  <!-- Menu -->
                  <div class="flex flex-shrink-0 space-x-2">
                    <Menu as="div" class="inline-block relative text-left">
                      <div>
                        <MenuButton
                            class="inline-flex p-1 text-gray-400 rounded-full border border-gray-400 hover:text-gray-600 hover:border-gray-600 focus:outline-none focus:ring-1 focus:gray-400"
                            title="Additional actions">
                          <span class="sr-only">Open options</span>
                          <DotsHorizontalIcon class="w-8 h-8" aria-hidden="true"/>
                        </MenuButton>
                      </div>

                      <transition enter-active-class="transition duration-100 ease-out"
                                  enter-from-class="opacity-0 transform scale-95"
                                  enter-to-class="opacity-100 transform scale-100"
                                  leave-active-class="transition duration-75 ease-in"
                                  leave-from-class="opacity-100 transform scale-100"
                                  leave-to-class="opacity-0 transform scale-95">
                        <MenuItems class="absolute bottom-4 left-12 mt-2 w-56 bg-white rounded-md ring-1 ring-black ring-opacity-5 shadow-lg origin-top-right focus:outline-none divide-y divide-gray-200">
                          <div class="py-1">
                            <MenuItem v-slot="{ active }" v-if="!isInRelevantChannel && !isInNotRelevantChannel">
                              <button
                                  :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'flex px-4 py-2 text-sm w-full']"
                                  @click.stop="openSaveToListDialog=true"
                                  title="Save company in a list">
                                <HeartIcon class="mr-3 w-5 h-5 text-gray-400" aria-hidden="true"/>
                                <span>Save to list</span>
                              </button>
                            </MenuItem>

                            <MenuItem v-slot="{ active }" v-if="!isInReportChannel">
                              <button
                                  :set="countReports = $parent.companyReportsCount(company)"
                                  :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'flex px-4 py-2 text-sm w-full']"
                                  @click.stop="$emit('request-report-action-report', {company : company})"
                                  title="Request a detailed company report">
                                <DownloadIcon class="mr-3 w-5 h-5 text-gray-400" aria-hidden="true"/>
                                <span>Ask a report</span>
                                <span v-show="countReports > 0" class="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {{ countReports }}
                                </span>
                              </button>
                            </MenuItem>
<!--                            <MenuItem v-slot="{ active }">-->
<!--                              <button-->
<!--                                  :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'flex px-4 py-2 text-sm w-full']"-->
<!--                                  @click.stop="$emit('add-to-benchmark-action', {company : company})"-->
<!--                                  title="Add the company to my benchmark list">-->
<!--                                <PlusCircleIcon class="mr-3 w-5 h-5 text-gray-400" aria-hidden="true"/>-->
<!--                                <span>Add to benchmarking</span>-->
<!--                              </button>-->
<!--                            </MenuItem>-->

                            <MenuItem v-slot="{ active }" v-if="isSineroaUser">
                              <button
                                  :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'flex px-4 py-2 text-sm w-full']"
                                  @click.stop="setCompanyForCopyCompanyPopup(company); setOpenCompanyCopyPopup(true);"
                                  title="Copy">
                                <DocumentDuplicateIcon class="mr-3 w-5 h-5 text-gray-400" aria-hidden="true"/>
                                <span>Copy</span>
                              </button>
                            </MenuItem>
                          </div>

                          <div class="py-1" v-if="isInCustomCompanyChannel">
                            <MenuItem v-slot="{ active }" >
                              <button
                                  :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'flex px-4 py-2 text-sm w-full']"
                                  @click.stop="$emit('remove-company-from-channel', {company : company})"
                                  title="Remove from the list">
                                <TrashIcon class="mr-3 w-5 h-5 text-gray-400 text-red-500" aria-hidden="true"/>
                                <span>Remove from the list</span>
                              </button>
                            </MenuItem>
                          </div>
                        </MenuItems>
                      </transition>
                    </Menu>
                  </div>
                </span>



                <span class="inline-flex items-center text-sm">
                  <button
                      @click="$emit('close')"
                      type="button"
                      class="flex justify-center items-center px-4 py-1 ml-6 w-full text-base font-medium text-white bg-gray-500 rounded-md border border-transparent hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-gray-500"
                      title="Close this company details window">
                    Close
                  </button>
                </span>
              </div>
            </div>

          </div>
        </TransitionChild>


      </div>
    </Dialog>
  </TransitionRoot>

</template>

<script>

import SaveToList from "@/components/SaveToList";
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot, Menu, MenuButton, MenuItem, MenuItems,} from '@headlessui/vue'
import {ExclamationIcon, OfficeBuildingIcon, XIcon, PlusCircleIcon, TrashIcon, DocumentDuplicateIcon} from '@heroicons/vue/outline'
import {BanIcon, DownloadIcon, HeartIcon, DotsHorizontalIcon} from '@heroicons/vue/solid'
import SimpleLoader from "@/components/SimpleLoader";
import {mapGetters, mapMutations} from "vuex";
import SectionNav from "@/components/Navigation/SectionNav";
import CompanyOverview from "@/components/ContentCard/CompanyOverview";
import CompanyPolling from "@/components/ContentCard/CompanyPolling";
import SimpleRejectCompanyDialog from "@/components/Polling/SimpleRejectCompanyDialog";
import _get from "lodash/get";

export default {
  name: "CompanyContent",
  components: {
    SimpleRejectCompanyDialog,
    SaveToList,
    CompanyOverview,
    CompanyPolling,
    SectionNav,
    SimpleLoader,
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    ExclamationIcon,
    XIcon,
    HeartIcon,
    BanIcon,
    DownloadIcon,
    OfficeBuildingIcon,
    DotsHorizontalIcon,
    PlusCircleIcon,
    TrashIcon,
    DocumentDuplicateIcon,
  },
  props: ['open', 'company', 'tagsReferential'],
  emits: ['close', 'use-tag', 'display-save-to-list-dialog', 'request-report-action-report', 'add-to-benchmark-action', 'remove-company-from-channel', , 'attachments:add', 'attachments:remove'],
  data() {
    return {
      blockUI: false,
      errorsToShow: [],

      companyDetails: undefined,
      companyRelatedNews: [
        // {
        //   id: 1,
        //   title: 'Velit placeat sit ducimus non sed',
        //   sourceUrl: 'https://www.google.com/',
        //   time: '1d ago',
        //   datetime: '2021-01-27T16:35',
        //   excerpt:
        //       'Doloremque dolorem maiores assumenda dolorem facilis. Velit vel in a rerum natus facere. Enim rerum eaque qui facilis. Numquam laudantium sed id dolores omnis in. Eos reiciendis deserunt maiores et accusamus quod dolor.',
        // },
        // {
        //   id: 2,
        //   title: 'Velit placeat sit ducimus non sed',
        //   sourceUrl: 'https://www.google.com/',
        //   time: '1d ago',
        //   datetime: '2021-01-27T16:35',
        // },

        {
          "source": {
            "id": null,
            "name": "Yahoo Entertainment"
          },
          "author": "Agbotic",
          "title": "Duncan Family Farms and Agbotic Plan Distributed SmartFarms",
          "description": "Companies to jointly deploy carbon-smart farm modelSACKETS HARBOR, N.Y., Feb. 18, 2022 (GLOBE NEWSWIRE) -- Duncan Family Farms® (DFF), one of North America's...",
          "url": "https://finance.yahoo.com/news/duncan-family-farms-agbotic-plan-001900289.html",
          "urlToImage": "https://s.yimg.com/cv/apiv2/social/images/yahoo_default_logo-1200x1200.png",
          "publishedAt": "2022-02-19T00:19:00Z",
          "content": "Companies to jointly deploy carbon-smart farm model\r\nSACKETS HARBOR, N.Y., Feb. 18, 2022 (GLOBE NEWSWIRE) -- Duncan Family Farms® (DFF), one of North America's largest growers of organic leafy greens… [+3360 chars]"
        },
        {
          "source": {
            "id": null,
            "name": "PRNewswire"
          },
          "author": null,
          "title": "With Market Size Valued At $4.6 Billion By 2026, It`s A Healthy Outlook For The Global Aerial Imaging Market",
          "description": "SAN FRANCISCO, Feb. 18, 2022 /PRNewswire/ -- A new market study published by Global Industry Analysts Inc., (GIA) the premier market research company, today released its report titled \"Aerial Imaging - Global Market Trajectory & Analytics\". The report present…",
          "url": "https://www.prnewswire.com/news-releases/with-market-size-valued-at-4-6-billion-by-2026--its-a-healthy-outlook-for-the-global-aerial-imaging-market-301485478.html",
          "urlToImage": "https://mma.prnewswire.com/media/1748885/MCP10090.jpg?p=facebook",
          "publishedAt": "2022-02-18T15:40:00Z",
          "content": "What's New for 2022?\r\n<ul><li>Global competitiveness and key competitor percentage market shares </li><li>Market presence across multiple geographies - Strong/Active/Niche/Trivial </li><li>Online int… [+8409 chars]"
        },
      ],

      openSaveToListDialog: false,

      currentTab: null,
      tabs: [
        {name: 'Overview', href: '#', current: true},
        // {name: 'Similar Companies', href: '#', current: false},
        // {name: 'Interactions', href: '#', current: false},
        // {name: 'Notes', href: '#', current: false},
        // {name: 'Attachments', href: '#', current: false},
        // {name: 'Polling', href: '#', current: false},
      ],



      openRejectCompanyDialog: false,
      companyToReject: null,

    }
  },
  created() {
    this.currentTab = this.tabs[0];
  },
  methods: {
    log(value) {
      console.log(value)
    },
    setCurrentTab(currentTab) {
      if (typeof currentTab === "string") {
        currentTab = this.tabs.find(n => n.name === currentTab);
      }
      if (typeof currentTab !== 'object') {
        return;
      }

      this.currentTab = currentTab;
      this.tabs.map(function (tab) {
        tab.current = tab === currentTab
      })
    },

    retrieveCompanyDetails() {
      if (this.companyDetails !== undefined && this.companyDetails.name === this.company.name) {
        return;
      }

      // Reinit some value
      this.errorsToShow = [];
      this.activeFinancialTabs = "1";

      if (this.blockUI) {
        return;
      } else {
        this.blockUI = true;
      }

      this.$axios
          .get('/entities/' + this.company.id, {}).then(function (response) {
            this.companyDetails = response.data;
            this.blockUI = false;
          }.bind(this))
          .catch(function (error) {
            // console.log(error)
            this.blockUI = false;
            this.companyDetails = undefined;
            this.errorsToShow.push('An error occurred while retrieving the information. Not all company information is displayed. Please try again in a few seconds.');
            throw error
          }.bind(this));
    },
    retrieveCompanyRelatedNews() {
      if (this.companyDetails !== undefined && this.companyDetails.name === this.company.name) {
        return;
      }

      this.$store
          .dispatch('retrieveCompanyNewsFor', {
            companyName: this.company.name,
          })
          .then(function (news) {
            this.companyRelatedNews = news;
          }.bind(this))

      // getNewsFor(this.company.name).then(function (news) {
      //   console.log(news)
      //   this.companyRelatedNews = news;
      // }.bind(this));

      // this.$axios
    // .get(this.$apiUrl + 'entities/' + this.company.id + '/news', {}).then(function (response) {
      //       this.companyRelatedNews = response.data;
      //     }.bind(this)
      // ).catch(function (error) {
      //       console.log(error)
      //       this.companyRelatedNews = undefined;
      //       this.errorsToShow.push('An error occurred while retrieving the information. Not all company information is displayed. Please try again in a few seconds.');
      //       throw new Error('' + error.response.status)
      //     }.bind(this)
      // );
    },

    removeCompanyFromChannel($event, company) {
      this.$store.dispatch('removeCompanyFromChannel', {
        company: company,
        channelUuid: this.getChannelUuid(this.currentViewChannel)
      }).then(function() {

        this.search(true, {
          slugs: this.getChannelCompanies(this.currentViewChannel)
        });

        this.removeRelevantCompanyToChannel($event, company);
      }.bind(this));
    },

    addRelevantCompanyToChannel(event, company) {
      this.$store.dispatch('addCompanyToChannel', {company: company, channelUuid: this.relevantCompaniesChannelUuid})
    },
    removeRelevantCompanyToChannel(event, company) {
      this.$store.dispatch('removeCompanyFromChannel', {
        company: company,
        channelUuid: this.relevantCompaniesChannelUuid
      })
    },
    addNotRelevantCompanyToChannel(event, company) {
      this.openRejectCompanyDialog = true;
      this.companyToReject = company;
      // this.$store.dispatch('addCompanyToChannel', {company: company, channelUuid: this.notRelevantCompaniesChannelUuid})
    },
    removeNotRelevantCompanyToChannel(event, company) {
      this.$store.dispatch('removeCompanyFromChannel', {
        company: company,
        channelUuid: this.notRelevantCompaniesChannelUuid
      })
    },


    ...mapMutations([
      'setOpenCompanyCopyPopup',
      'setCompanyForCopyCompanyPopup'
    ]),
  },

  watch: {
    open(value) {
      if (value === true) {
        this.retrieveCompanyDetails();
        this.retrieveCompanyRelatedNews();
      }
    },
  },

  computed: {
    isSineroaUser() {
      const profile = _get(this.profiles, this.$store.getters.getCurrentWorkspaceSlug, null);
      return _get(profile, 'analystUser', true) ||  _get(profile, 'adminUser', true) ||  _get(profile, 'superAdminUser', true)
    },

    currentProperties() {
      if (this.currentTab.name === 'Overview') {
        return {
          companyDetails: this.companyDetails,
          company: this.company,
          relatedNews: this.companyRelatedNews,
          tagsReferential: this.tagsReferential,
          memberProfile: this.currentMemberProfile,
        }
      }

      if (this.currentTab.name === 'Polling') {
        return {
          company: this.company,
          companyDetails: this.companyDetails,
        }
      }

      return {};
    },
    ...mapGetters({
      currentViewChannel: "getCurrentViewChannel",
      getChannelUuid: 'getChannelUuid',
      relevantCompaniesChannelUuid: 'getRelevantCompaniesChannelUuid',
      notRelevantCompaniesChannelUuid: 'getNotRelevantCompaniesChannelUuid',
      relevantCompanies: 'getRelevantCompanies',
      notRelevantCompanies: 'getNotRelevantCompanies',
      currentMemberProfile: 'getCurrentMemberProfile',
      isInCustomCompanyChannel: 'isInCustomCompanyChannel',
      isInNotRelevantChannel: 'isInNotRelevantChannel',
      isInRelevantChannel: 'isInRelevantChannel',
      isInReportChannel: 'isInReportChannel',
    }),
  }
}
</script>
